import { useNavigate } from 'react-router'
import clsx from 'clsx'
import { AspectRatio, Table, Image } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconArrowDown, IconArrowUp, IconPoint } from '@tabler/icons-react'
import { getRankSuffix } from '../../common/helpers'
import { TeamStats } from '../../teams/api'
import { useCompetitionId } from '../../common/hooks'
import { Typography } from '../../common/typography'
import RecentForm from '../RecentForm/RecentForm'
import classes from './Ladder.module.css'

export default function LadderComponent({
  stats,
  active,
  divisionLadder,
}: {
  stats: TeamStats[]
  active?: string
  divisionLadder: boolean
}) {
  const navigate = useNavigate()
  const competitionId = useCompetitionId()
  const isMobile = useMediaQuery('(max-width: 800px)')
  const short = active === 'full' || active === 'short' || !active
  const form = active === 'form' || active === 'full' || !active
  const shouldStickFirstColumn = isMobile && active === 'full'

  const handleRowClick = (teamId: number) => {
    navigate(`/competitions/${competitionId}/teams/${teamId}/overview`)
  }

  // rank/division_rank is null (no leaderboard row yet = no ended matches) - and
  // 0 on older API responses. Either way treat as "unranked": sort last, not 0th.
  const getRank = (ts: (typeof stats)[number]) =>
    divisionLadder ? ts.division_rank : ts.rank
  const rankOrLast = (ts: (typeof stats)[number]) => {
    const r = getRank(ts)
    return r && r > 0 ? r : Infinity
  }

  const sortedStats = [...stats].sort((a, b) => rankOrLast(a) - rankOrLast(b))

  const rows = sortedStats.map((ts) => {
    const rank = divisionLadder ? ts.division_rank : ts.rank
    const change = divisionLadder ? ts.division_rank_change : ts.rank_change
    const isRanked = rank != null && rank > 0

    return (
      <Table.Tr key={ts.team_id} onClick={() => handleRowClick(ts.team_id)}>
        <Table.Td>
          <div className={classes.team}>
            <Typography lh="150" ta="left">
              {rank != null && rank > 0 ? (
                <>
                  {rank}
                  <sup>{getRankSuffix(rank)}</sup>
                </>
              ) : (
                '–'
              )}
            </Typography>
            {isRanked && change === 0 && (
              <div className={classes.rank_update}>
                <IconPoint className={classes.icon} />
              </div>
            )}
            {isRanked && change > 0 && (
              <div className={classes.rank_update}>
                <IconArrowUp
                  className={clsx(classes.icon, classes.up, {
                    [classes.iconMobile]: isMobile,
                  })}
                />
                <Typography
                  fs="sm"
                  ff="JetBrains Mono"
                  fw={500}
                  ta="center"
                  className={classes.up}
                >
                  {`+${change}`}
                </Typography>
              </div>
            )}
            {isRanked && change < 0 && (
              <div className={classes.rank_update}>
                <IconArrowDown
                  className={clsx(classes.icon, classes.down, {
                    [classes.iconMobile]: isMobile,
                  })}
                />
                <Typography
                  fs="sm"
                  ff="JetBrains Mono"
                  fw={500}
                  ta="center"
                  className={classes.down}
                >
                  {change}
                </Typography>
              </div>
            )}
          </div>
        </Table.Td>
        <Table.Td>
          <AspectRatio ratio={1 / 1} className={classes.teamLogoCell}>
            <Image
              src={ts.team_logo.thumbnail}
              alt={ts.team_name}
              className={clsx(classes.teamLogo, {
                [classes.teamLogoMobile]: isMobile,
              })}
              fit="scale-down"
            />
          </AspectRatio>
        </Table.Td>
        <Table.Td className={classes.teamName}>
          <Typography lh="150">{ts.team_name}</Typography>
        </Table.Td>
        {short && (
          <Table.Td>
            <Typography ff="JetBrains Mono" lh="140" ta="center">
              {ts.points}
            </Typography>
          </Table.Td>
        )}
        {(active === 'full' || !active) && (
          <>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.total_matches}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.matches_won}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.matches_draw}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.matches_lost}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.goals_for}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.goals_against}
              </Typography>
            </Table.Td>
            <Table.Td>
              <Typography ff="JetBrains Mono" lh="140" ta="center">
                {ts.goal_difference}
              </Typography>
            </Table.Td>
          </>
        )}
        {form && (
          <Table.Td>
            <RecentForm last_five={ts.last_5_matches} />
          </Table.Td>
        )}
      </Table.Tr>
    )
  })

  return (
    <div className={classes.tableContainer}>
      <Table.ScrollContainer minWidth={300}>
        <Table
          classNames={{
            table: clsx(
              classes.table,
              shouldStickFirstColumn && classes.stickyFirstColumn
            ),
            tr: classes.tr,
            th: classes.th,
            td: classes.td,
          }}
          verticalSpacing="xs"
          withRowBorders={false}
        >
          <Table.Thead>
            <Table.Tr className={classes.headerRow}>
              <Table.Th>Pos</Table.Th>
              <Table.Th></Table.Th>
              <Table.Th style={{ textAlign: 'left' }}>Team</Table.Th>
              {short && <Table.Th>Pts</Table.Th>}

              {(active === 'full' || !active) && (
                <>
                  <Table.Th>P</Table.Th>
                  <Table.Th>W</Table.Th>
                  <Table.Th>D</Table.Th>
                  <Table.Th>L</Table.Th>
                  <Table.Th>GF</Table.Th>
                  <Table.Th>GA</Table.Th>
                  <Table.Th>GD</Table.Th>
                </>
              )}
              {form && <Table.Th>Form</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  )
}
